import { useNav } from "@pomle/react-router-paths";
import { isOnboardingComplete } from "lib/onboarding/isOnboardingComplete";
import { findNotParticipatedStudies } from "lib/studies/findNotParticipatedStudies";
import { useCallback, useMemo } from "react";
import { useOnboardingQuery } from "render/hooks/api/queries/useOnboardingQuery";
import { usePatientConsentsQuery } from "render/hooks/api/queries/usePatientConsentsQuery";
import { useTracking } from "render/hooks/useTracking";
import { paths, queries } from "render/routes/paths";
import { Expandable } from "render/ui/presentation/Expandable";
import { ActionButton } from "render/ui/trigger/ActionButton";
import { useConsentsMap } from "render/views/ProfileView/components/DataAndPrivacySection/hooks/useConsentsMap";
import { Stage } from "../../../../../AppointmentOnboarding";
import { appointmentEvents } from "../../tracking";
import { Trans } from "../../trans";
import styles from "./styles.module.sass";

interface CompleteProfileButtonProps {
  slotId: string;
}

export function CompleteProfileButton({ slotId }: CompleteProfileButtonProps) {
  const { trackEvent } = useTracking();
  const nav = {
    appointmentOnboarding: useNav(
      paths.appointmentOnboarding,
      queries.onboarding
    ),
  };
  const onboardingQuery = useOnboardingQuery();
  const patientConsentsQuery = usePatientConsentsQuery();
  const { studies } = useConsentsMap();
  const showButton = useMemo(() => {
    if (!onboardingQuery.isFetched || patientConsentsQuery.data == null) {
      return false;
    }

    return !isOnboardingComplete({
      patientConsents: patientConsentsQuery.data,
      onboardingData: onboardingQuery.data,
      requiredStudies: studies,
    });
  }, [
    onboardingQuery.data,
    onboardingQuery.isFetched,
    patientConsentsQuery.data,
    studies,
  ]);
  const isOnboardingCompleted = useMemo(() => {
    return onboardingQuery.data != null;
  }, [onboardingQuery.data]);
  const allStudiesAgreed = useMemo(() => {
    if (patientConsentsQuery.data == null) {
      return false;
    }

    return (
      findNotParticipatedStudies(patientConsentsQuery.data, studies).length ===
      0
    );
  }, [patientConsentsQuery.data, studies]);

  const onClick = useCallback(() => {
    trackEvent(appointmentEvents.completeProfileClick());
    const initialStep: Stage[] = [];
    if (!allStudiesAgreed) {
      initialStep.push(Stage.Studies);
    } else if (!isOnboardingCompleted) {
      initialStep.push(Stage.Onboarding);
    }

    nav.appointmentOnboarding.go({ slotId }, { initialStep });
  }, [
    trackEvent,
    allStudiesAgreed,
    isOnboardingCompleted,
    slotId,
    nav.appointmentOnboarding,
  ]);

  return (
    <Expandable isOpen={showButton}>
      <ActionButton
        onClick={onClick}
        variant="suggestion"
        style={{ background: "rgba(1, 157, 201, 0.8)" }}
      >
        <div className={styles.profileCompleteButtonText}>
          <div>
            <Trans.CompleteProfileButton.Title />
          </div>
          <div className={styles.subText}>
            <Trans.CompleteProfileButton.Sub />
          </div>
        </div>
      </ActionButton>
    </Expandable>
  );
}
